import { formatNumber } from '@physlab/core';
import { difficultyInfo } from './questionUtils.js';

const TYPE_LABEL = {
  basic: '基础',
  standard: '进阶',
  challenge: '挑战',
};

function timestamp(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value);
}

function fileTimestamp(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}-${pad(value.getHours())}${pad(value.getMinutes())}`;
}

function valueText(value, precision = 2) {
  if (!Number.isFinite(value)) return '—';
  return formatNumber(value, precision);
}

function yamlEscape(value) {
  return JSON.stringify(String(value));
}

function renderParamsTable(model, params) {
  const rows = model.schema.map((field) => {
    const value = params[field.key];
    const display = field.precision === 0 ? Number(value).toFixed(0) : Number(value).toFixed(field.precision ?? 2);
    return `| $${field.symbol}$ | ${field.label} | ${display} | ${field.unit || '—'} | ${field.min}–${field.max} |`;
  });
  return ['| 符号 | 参数 | 当前值 | 单位 | 可调范围 |', '| --- | --- | ---: | --- | --- |', ...rows].join('\n');
}

function renderReadouts(metrics) {
  const rows = metrics.readouts.map(
    (item) => `| ${item.label} | ${valueText(item.value, item.precision ?? 2)} | ${item.unit || '—'} |`,
  );
  rows.push(
    `| 动能 | ${valueText(metrics.energy.kinetic, 3)} | J |`,
    `| 势能 | ${valueText(metrics.energy.potential, 3)} | J |`,
    `| 机械能 | ${valueText(metrics.energy.total, 3)} | J |`,
  );
  if (metrics.momentum) rows.push(`| 总动量 | ${valueText(metrics.momentum.total, 3)} | kg·m/s |`);
  return ['| 物理量 | 当前值 | 单位 |', '| --- | ---: | --- |', ...rows].join('\n');
}

function renderRecordTable(model) {
  const table = model.meta.recordTable;
  if (!table) return '';
  const header = `| ${table.columns.join(' | ')} |`;
  const divider = `| ${table.columns.map(() => '---').join(' | ')} |`;
  const rows = Array.from({ length: table.rows }, (_, row) => {
    const cells = table.columns.map((_, col) => (col === 0 ? row + 1 : ' '));
    return `| ${cells.join(' | ')} |`;
  });
  return [`**${table.title}**`, '', header, divider, ...rows].join('\n');
}

function questionHeading(type, index, question) {
  return `### ${type} ${index}｜${question.topic}（${TYPE_LABEL[question.difficulty] ?? '进阶'}）`;
}

function renderQuestionBody(question, includeAnswers) {
  const parts = [question.prompt];
  if (!includeAnswers) {
    parts.push('', '**作答区：**', '', '```text', '（在此写出公式、代入过程与单位）', '', '', '', '```');
  }
  return parts.join('\n');
}

function renderAnswer(question, index, type) {
  const steps = question.solution.map((step, i) => `${i + 1}. ${step}`).join('\n');
  return [
    `### ${type} ${index}｜${question.topic}`,
    '',
    `**标准答案：** ${question.answer}`,
    '',
    '**解析：**',
    '',
    steps,
    '',
    `**知识点：** ${question.knowledge.join('、')}`,
    '',
  ].join('\n');
}

/**
 * 生成标准化实验指导书（Markdown）。
 * 教师版包含全部标准答案解析；学生版留出作答区。
 */
export function buildLabGuide({
  model,
  params,
  metrics,
  exerciseSet,
  includeAnswers = true,
  generatedAt = new Date(),
  agentVersion = '1.0.0',
}) {
  const date = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const meta = model.meta;
  const difficulty = difficultyInfo(exerciseSet?.difficulty ?? 'standard');
  const exercises = exerciseSet?.exercises ?? [];
  const thinking = exerciseSet?.thinking ?? [];
  const all = [...exercises, ...thinking];
  const mode = includeAnswers ? '教师版 · 含标准答案解析' : '学生版 · 含作答区';
  const lines = [];

  lines.push('---');
  lines.push(`title: ${yamlEscape(`${meta.title} · 实验指导书`)}`);
  lines.push(`experiment: ${yamlEscape(model.id)}`);
  lines.push(`experiment_code: ${yamlEscape(`PHY-${model.id.toUpperCase()}`)}`);
  lines.push(`difficulty: ${yamlEscape(difficulty.label)}`);
  lines.push(`edition: ${yamlEscape(mode)}`);
  lines.push(`generated_at: ${yamlEscape(timestamp(date))}`);
  lines.push(`generator: ${yamlEscape(`PhysLab Teaching Agent v${agentVersion}`)}`);
  lines.push('params:');
  for (const field of model.schema) {
    lines.push(`  ${field.key}: ${params[field.key]}`);
  }
  lines.push('---');
  lines.push('');
  lines.push(`# ${meta.title} · 实验指导书`);
  lines.push('');
  lines.push(`> ${meta.tagline}`);
  lines.push('');
  lines.push(
    `**学科章节：** ${meta.grade} ｜ **实验编号：** PHY-${model.id.toUpperCase()} ｜ **版本：** ${mode}`,
  );
  lines.push(
    `**建议时长：** ${meta.duration} ｜ **生成时间：** ${timestamp(date)} ｜ **难度：** ${difficulty.label}`,
  );
  lines.push('');
  lines.push(meta.description);
  lines.push('');

  lines.push('## 一、实验目的');
  lines.push('');
  for (const item of meta.objectives) lines.push(`- ${item}`);
  lines.push('');

  lines.push('## 二、实验原理');
  lines.push('');
  for (const item of meta.theory) {
    lines.push(item);
    lines.push('');
  }
  lines.push('### 核心公式');
  lines.push('');
  lines.push('| 物理量 | 公式 | 说明 |');
  lines.push('| --- | --- | --- |');
  for (const formula of meta.formulas) {
    lines.push(`| ${formula.label} | $${formula.latex}$ | ${formula.note} |`);
  }
  lines.push('');

  lines.push('## 三、实验器材');
  lines.push('');
  lines.push(meta.apparatus.map((item) => `- ${item}`).join('\n'));
  lines.push('');

  lines.push('## 四、实验参数');
  lines.push('');
  lines.push('以下参数与生成本指导书时的仿真状态一致：');
  lines.push('');
  lines.push(renderParamsTable(model, params));
  lines.push('');

  lines.push('## 五、实验步骤');
  lines.push('');
  meta.procedure.forEach((step, index) => lines.push(`${index + 1}. ${step}`));
  lines.push('');

  lines.push('## 六、数据记录');
  lines.push('');
  lines.push(`### 6.1 仿真实时快照（t = ${valueText(metrics.time, 2)} s）`);
  lines.push('');
  lines.push(renderReadouts(metrics));
  lines.push('');
  if (metrics.status) {
    lines.push(`**运行状态：** ${metrics.status.label}。${metrics.status.detail ?? ''}`);
    lines.push('');
  }
  lines.push('### 6.2 课堂记录表');
  lines.push('');
  lines.push(renderRecordTable(model));
  lines.push('');

  lines.push('## 七、误差分析与注意事项');
  lines.push('');
  lines.push('### 7.1 安全与操作提示');
  lines.push('');
  for (const item of meta.safety) lines.push(`- ${item}`);
  lines.push('');
  lines.push('### 7.2 误差来源');
  lines.push('');
  lines.push('- **模型误差：** 仿真采用理想化模型（如忽略空气阻力、轨道摩擦），与真实实验存在系统差异。');
  lines.push('- **数值误差：** 固定步长积分会引入微小截断误差；本平台实时监测机械能与动量偏差，超过阈值会自动告警并细化步长。');
  lines.push('- **测量误差：** 长度、时间、速度的读数精度会影响结果，应多次测量取平均并按有效数字记录。');
  lines.push('- **参数误差：** 摆角、倾角、质量等参数应使用校准后的仪器测量，避免把摆线长度当作摆长。');
  lines.push('');

  lines.push('## 八、课堂练习题');
  lines.push('');
  if (exercises.length === 0) lines.push('（本次未生成练习题）');
  exercises.forEach((question, index) => {
    lines.push(questionHeading('练习', index + 1, question));
    lines.push('');
    lines.push(renderQuestionBody(question, includeAnswers));
    lines.push('');
  });

  lines.push('## 九、思考题');
  lines.push('');
  if (thinking.length === 0) lines.push('（本次未生成思考题）');
  thinking.forEach((question, index) => {
    lines.push(questionHeading('思考', index + 1, question));
    lines.push('');
    lines.push(renderQuestionBody(question, includeAnswers));
    lines.push('');
  });

  if (includeAnswers) {
    lines.push('## 十、参考答案与解析');
    lines.push('');
    lines.push('> 以下答案由教学 Agent 根据本指导书参数计算生成，数值保留合理有效数字；课堂讲评时请关注公式、代入过程与单位。');
    lines.push('');
    exercises.forEach((question, index) => {
      lines.push(renderAnswer(question, index + 1, '练习'));
    });
    thinking.forEach((question, index) => {
      lines.push(renderAnswer(question, index + 1, '思考'));
    });

    lines.push('## 十一、评分建议');
    lines.push('');
    lines.push('| 题型 | 建议分值 | 评分关注点 |');
    lines.push('| --- | ---: | --- |');
    lines.push(`| 课堂练习（${exercises.length} 题） | ${Math.max(40, exercises.length * 10)} 分 | 公式选择、代入过程、单位与有效数字 |`);
    lines.push(`| 思考题（${thinking.length} 题） | ${Math.max(20, thinking.length * 10)} 分 | 物理概念、因果解释、实验方案合理性 |`);
    lines.push('| 实验记录与误差分析 | 20 分 | 数据完整性、图像处理、误差来源分析 |');
    lines.push('');
  }

  lines.push('---');
  lines.push('');
  lines.push(
    `本指导书由 **PhysLab Teaching Agent v${agentVersion}** 基于「${meta.title}」当前仿真参数自动生成。公式与数值均来自确定性物理模型；课堂使用前建议教师结合本校器材进行复核。`,
  );
  lines.push('');
  return {
    markdown: lines.join('\n'),
    filename: `physlab-${model.id}-${includeAnswers ? '教师版' : '学生版'}-${fileTimestamp(date)}.md`,
    title: `${meta.title} · 实验指导书（${mode}）`,
  };
}
